import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface User {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  role: string
  organization_id: number | null
  tenant_id: number | null
  first_login: boolean
  is_active: boolean
  created_at: string
  last_login: string | null
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  firstLogin: (email: string, pin: string, password: string) => Promise<void>
  adminLogin: (email: string, password: string) => Promise<void>
  logout: () => void
  isAdmin: boolean
  isResident: boolean
  isTenantAdmin: boolean
  isSuperAdmin: boolean
  showAdminUI: boolean      // True when user should see admin dashboard (role-based, not subdomain-based)
  portalType: 'admin' | 'resident' | 'unknown'
  tenantSlug: string
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

function getSubdomain(): string {
  const host = window.location.hostname
  if (host.includes('.ovrwatch.app')) {
    return host.split('.')[0].toLowerCase()
  }
  // Local dev - default to resident portal
  return ''
}

function getPortalType(): 'admin' | 'resident' | 'unknown' {
  const sub = getSubdomain()
  if (sub === 'admin') return 'admin'
  if (sub && sub !== 'www' && sub !== '') return 'resident'
  return 'unknown'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'))
  const [isLoading, setIsLoading] = useState(true)

  const portalType = getPortalType()
  const tenantSlug = getSubdomain()

  useEffect(() => {
    if (token) {
      fetchProfile()
    } else {
      setIsLoading(false)
    }
  }, [])

  async function fetchProfile(overrideToken?: string) {
    const activeToken = overrideToken || token
    try {
      // Always try /api/auth/me first — works for ALL user types (admin, tenant_admin, resident)
      const meRes = await fetch('/api/auth/me', {
        headers: { Authorization: `Bearer ${activeToken}` }
      })

      if (meRes.ok) {
        const userData = await meRes.json()
        const role = userData.role || ''

        // For admin-level roles, /api/auth/me is all we need
        if (['super_admin', 'admin', 'tenant_admin', 'manager', 'staff'].includes(role)) {
          setUser(userData)
          return
        }

        // For residents, also fetch tenant-specific data for the resident portal
        if (role === 'resident') {
          const residentRes = await fetch('/api/auth/resident/profile', {
            headers: { Authorization: `Bearer ${activeToken}` }
          })
          if (residentRes.ok) {
            const data = await residentRes.json()
            setUser(data.user ? {
              ...data.user,
              first_name: data.tenant?.first_name || userData.first_name,
              last_name: data.tenant?.last_name || userData.last_name,
              is_active: true,
              created_at: userData.created_at || '',
              last_login: userData.last_login || null,
            } : userData)
          } else {
            // Resident profile failed, but we still have basic user data
            setUser(userData)
          }
          return
        }

        // Unknown role — use whatever /me returned
        setUser(userData)
      } else {
        // Token invalid/expired
        localStorage.removeItem('token')
        setToken(null)
        setUser(null)
      }
    } catch {
      localStorage.removeItem('token')
      setToken(null)
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  async function login(email: string, password: string) {
    const res = await fetch('/api/auth/resident/login/password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantSlug || 'default',
      },
      body: JSON.stringify({ email, password })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(err.detail)
    }
    const data = await res.json()
    localStorage.setItem('token', data.access_token)
    setToken(data.access_token)
    if (data.user) setUser(data.user)
    else await fetchProfile(data.access_token)
  }

  async function firstLogin(email: string, pin: string, password: string) {
    const res = await fetch('/api/auth/resident/setup-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant-ID': tenantSlug || 'default',
      },
      body: JSON.stringify({ email, pin: pin, new_password: password })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'First login failed' }))
      throw new Error(err.detail)
    }
    const data = await res.json()
    localStorage.setItem('token', data.access_token)
    setToken(data.access_token)
    // Show one-time PIN reminder modal after first-time setup.
    localStorage.setItem('show_pin_reminder', '1')
    if (data.user) setUser(data.user)
    else await fetchProfile(data.access_token)
  }

  async function adminLogin(email: string, password: string) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(err.detail)
    }
    const data = await res.json()
    localStorage.setItem('token', data.access_token)
    setToken(data.access_token)
    setUser(null)
    // Fetch full profile
    const meRes = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${data.access_token}` }
    })
    if (meRes.ok) {
      setUser(await meRes.json())
    }
  }

  function logout() {
    localStorage.removeItem('token')
    setToken(null)
    setUser(null)
  }

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin'
  const isTenantAdmin = user?.role === 'tenant_admin'
  const isAdmin = isSuperAdmin || isTenantAdmin
  const isResident = user?.role === 'resident'

  // Determine if user should see admin UI — based on ROLE, not subdomain
  // super_admin/admin on admin.ovrwatch.app → admin UI
  // tenant_admin/manager/staff on ANY subdomain → admin UI
  // resident → always resident UI
  const adminRoles = ['super_admin', 'admin', 'tenant_admin', 'manager', 'staff']
  const showAdminUI = !!user && adminRoles.includes(user.role)

  return (
    <AuthContext.Provider value={{
      user, token, isAuthenticated: !!token && !!user, isLoading,
      login, firstLogin, adminLogin, logout,
      isAdmin, isResident, isTenantAdmin, isSuperAdmin, showAdminUI,
      portalType, tenantSlug,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}

export default AuthContext
